<template>
  <div class="md-layout product-create-page">
    <div class="md-layout-item">
      <md-card class="form-card">
        <!-- Header -->
        <md-card-header class="form-card-header">
          <div class="header-icon-wrap"><md-icon>add_box</md-icon></div>
          <div>
            <h4 class="title">Create New Product</h4>
            <p class="subtitle">
              Fill in the details below to add a new product
            </p>
          </div>
        </md-card-header>

        <md-card-content>
          <!-- Steps Bar -->
          <div class="steps-bar">
            <div
              v-for="(step, index) in visibleSteps"
              :key="index"
              class="step-item"
              :class="{
                active: currentStep === index,
                done: currentStep > index,
              }"
              @click="goToStep(index)"
            >
              <div class="step-circle">
                <md-icon v-if="currentStep > index">check</md-icon>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>

          <!-- STEP 0 — Basic Info -->
          <div v-show="currentStep === 0" class="step-content">
            <h5 class="section-title">Basic Information</h5>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Product Name <span class="req">*</span></label
                >
                <input
                  v-model="form.name"
                  class="field-input"
                  placeholder="e.g. Samsung Galaxy A15"
                />
              </div>
              <div class="form-group">
                <label class="field-label"
                  >Display Name <span class="req">*</span></label
                >
                <input
                  v-model="form.displayName"
                  class="field-input"
                  placeholder="Shown to customers"
                />
              </div>
            </div>
            <div class="form-group">
              <label class="field-label"
                >Short Description <span class="req">*</span></label
              >
              <textarea
                v-model="form.shortDescription"
                class="field-input field-textarea"
                rows="3"
                placeholder="Brief description..."
              />
            </div>
            <div class="form-group">
              <label class="field-label">Long Description</label>
              <textarea
                v-model="form.longDescription"
                class="field-input field-textarea"
                rows="4"
                placeholder="Full product description..."
              />
            </div>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Product Type <span class="req">*</span></label
                >
                <select
                  v-model="form.productType"
                  class="field-input field-select"
                  @change="onTypeChange"
                >
                  <option value="Onetime">One-time</option>
                  <option value="Subscription">Subscription</option>
                  <option value="Lifetime Subscription">
                    Lifetime Subscription
                  </option>
                  <option value="Accessories">Accessories</option>
                </select>
              </div>
              <div
                class="form-group"
                v-if="form.productType === 'Subscription'"
              >
                <label class="field-label"
                  >Recurring Interval <span class="req">*</span></label
                >
                <select
                  v-model="form.recurringInterval"
                  class="field-input field-select"
                >
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div class="form-group" v-if="form.productType !== 'Accessories'">
                <label class="field-label"
                  >Product Category <span class="req">*</span></label
                >
                <select
                  v-model="form.productCategory"
                  class="field-input field-select"
                >
                  <option value="">- select -</option>
                  <option value="Phones">Phones</option>
                  <option value="Tablets">Tablets</option>
                </select>
              </div>
              <div class="form-group" v-if="form.productType === 'Accessories'">
                <label class="field-label"
                  >Product Category
                  <span class="optional">(optional)</span></label
                >
                <select
                  v-model="form.productCategory"
                  class="field-input field-select"
                >
                  <option value="">- select -</option>
                  <option value="Phones">Phones</option>
                  <option value="Tablets">Tablets</option>
                </select>
              </div>
            </div>
            <div
              class="form-row two-col"
              v-if="form.productType !== 'Accessories'"
            >
              <div class="form-group">
                <label class="field-label">GTIN</label>
                <input
                  v-model="form.gtin"
                  class="field-input"
                  placeholder="13-digit barcode"
                />
              </div>
            </div>
          </div>

          <!-- STEP 1 - Specs -->
          <div v-show="currentStep === 1" class="step-content">
            <h5 class="section-title">Device Specifications</h5>
            <p class="section-desc">
              Quick-entry fields for common specs, and custom key-value pairs
              below.
            </p>

            <div
              class="form-row two-col"
              v-if="form.productType !== 'Subscription'"
            >
              <div class="form-group">
                <label class="field-label">Camera</label>
                <input
                  v-model="form.camera"
                  class="field-input"
                  placeholder="e.g. 50MP + 5MP"
                />
              </div>
              <div class="form-group">
                <label class="field-label">Screen</label>
                <input
                  v-model="form.screen"
                  class="field-input"
                  placeholder="e.g. 6.5 FHD+ AMOLED"
                />
              </div>
            </div>
            <div
              class="form-row two-col"
              v-if="form.productType !== 'Subscription'"
            >
              <div class="form-group">
                <label class="field-label">Memory / Storage</label>
                <input
                  v-model="form.memory"
                  class="field-input"
                  placeholder="e.g. 4GB RAM / 128GB"
                />
              </div>
              <div class="form-group">
                <label class="field-label">Battery</label>
                <input
                  v-model="form.battery"
                  class="field-input"
                  placeholder="e.g. 5000 mAh"
                />
              </div>
            </div>

            <div class="specs-divider"><span>Custom Specifications</span></div>
            <div
              v-if="form.productSpecifications.length === 0"
              class="empty-specs"
            >
              <md-icon>list_alt</md-icon>
              <p>No custom specs yet</p>
            </div>
            <div
              v-for="(spec, specIndex) in form.productSpecifications"
              :key="specIndex"
              class="spec-row"
            >
              <input
                v-model="spec.key"
                class="field-input spec-input"
                placeholder="Key (e.g. OS)"
              />
              <input
                v-model="spec.value"
                class="field-input spec-input"
                placeholder="Value (e.g. Android 14)"
              />
              <button class="remove-spec-btn" @click="removeSpec(specIndex)">
                <md-icon>delete_outline</md-icon>
              </button>
            </div>
            <button class="add-spec-btn" @click="addSpec">
              <md-icon>add</md-icon> Add Specification
            </button>
          </div>

          <!-- STEP 2 - Pricing / Markets -->
          <div v-show="currentStep === 2" class="step-content">
            <h5 class="section-title">Pricing &amp; Markets</h5>
            <p class="section-desc">
              Set prices for each marketplace. Only enabled markets will be
              processed.
            </p>

            <div v-if="loadingMarkets" class="markets-loading">
              <md-progress-spinner
                md-mode="indeterminate"
                :md-diameter="24"
                :md-stroke="2"
              />
              <span>Loading marketplaces...</span>
            </div>

            <div v-else class="markets-table">
              <div v-if="marketRows.length === 0" class="info-banner">
                <md-icon>info_outline</md-icon>
                No marketplaces configured.
                <router-link to="/kid/wingio-marketplaces"
                  >Add marketplaces first.</router-link
                >
              </div>

              <div
                v-for="row in marketRows"
                :key="row.marketplaceId"
                class="market-price-row"
                :class="{ enabled: row.isEnabled }"
              >
                <div class="market-toggle">
                  <md-switch v-model="row.isEnabled" />
                </div>
                <div class="market-info">
                  <span class="market-flag">{{ row.icon }}</span>
                  <div>
                    <strong>{{ row.country }}</strong>
                    <small>{{ row.marketplaceCode }} · {{ row.currencyCode }}</small>
                  </div>
                  <span v-if="row.isDefault" class="default-pill">Default</span>
                </div>

                <div
                  class="market-fields"
                  :class="{ disabled: !row.isEnabled }"
                >
                  <div class="price-field">
                    <label>Price <span class="req">*</span></label>
                    <div class="input-prefix-wrap">
                      <span class="input-prefix">{{ row.currencySymbol }}</span>
                      <input
                        v-model.number="row.price"
                        type="number"
                        min="0"
                        step="0.01"
                        class="field-input prefix-input"
                        placeholder="0.00"
                        :disabled="!row.isEnabled"
                      />
                    </div>
                  </div>
                  <div class="price-field">
                    <label>Offer Price</label>
                    <div class="input-prefix-wrap">
                      <span class="input-prefix">{{ row.currencySymbol }}</span>
                      <input
                        v-model.number="row.offerPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        class="field-input prefix-input"
                        placeholder="0.00"
                        :disabled="!row.isEnabled"
                      />
                    </div>
                  </div>
                  <div class="price-field offer-text-field">
                    <label>Offer Label</label>
                    <input
                      v-model="row.offerPriceText"
                      class="field-input"
                      placeholder="e.g. Limited offer"
                      :disabled="!row.isEnabled"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- STEP - Compatible Products (Accessories only) -->
          <div
            v-if="form.productType === 'Accessories'"
            v-show="currentStep === compatStep"
            class="step-content"
          >
            <h5 class="section-title">Compatible Products</h5>
            <p class="section-desc">
              Select which one-time products this accessory belongs to.
            </p>
            <div v-if="onetimeProducts.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon> No one-time products found. Create
              some first.
            </div>
            <div class="parent-products-grid">
              <label
                v-for="p in onetimeProducts"
                :key="p._id"
                class="parent-product-card"
                :class="{ selected: form.parentProductIds.includes(p._id) }"
              >
                <input
                  type="checkbox"
                  :value="p._id"
                  v-model="form.parentProductIds"
                  style="display: none"
                />
                <div class="pp-avatar">
                  {{ (p.name || "?")[0].toUpperCase() }}
                </div>
                <div class="pp-info">
                  <strong>{{ p.displayName || p.name }}</strong>
                  <small>{{ p.name }}</small>
                </div>
                <md-icon class="pp-check">{{
                  form.parentProductIds.includes(p._id)
                    ? "check_circle"
                    : "radio_button_unchecked"
                }}</md-icon>
              </label>
            </div>
          </div>

          <!-- STEP - Shipping Methods -->
          <div v-show="currentStep === shippingStep" class="step-content">
            <h5 class="section-title">Shipping Methods</h5>
            <p class="section-desc">
              Select which shipping methods are available for this product.
              Leave all unchecked to allow all active methods.
            </p>

            <div v-if="loadingShipping" class="markets-loading">
              <md-progress-spinner
                md-mode="indeterminate"
                :md-diameter="24"
                :md-stroke="2"
              />
              <span>Loading shipping methods...</span>
            </div>

            <div v-else-if="shippingMethods.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon>
              No shipping methods configured.
              <router-link to="/kid/shipping">Add shipping methods first.</router-link>
            </div>

            <div v-else>
              <div
                class="shipping-all-banner"
                v-if="form.allowedShippingMethods.length === 0"
              >
                <md-icon>check_circle</md-icon>
                All active shipping methods are allowed for this product.
              </div>

              <div
                v-for="method in shippingMethods"
                :key="method._id"
                class="shipping-method-row"
                :class="{
                  selected: form.allowedShippingMethods.includes(method._id),
                }"
                @click="toggleShipping(method._id)"
              >
                <div class="shipping-check">
                  <md-icon>{{
                    form.allowedShippingMethods.includes(method._id)
                      ? "check_box"
                      : "check_box_outline_blank"
                  }}</md-icon>
                </div>
                <div class="shipping-icon-wrap">
                  <md-icon>{{ method.icon || "local_shipping" }}</md-icon>
                </div>
                <div class="shipping-info">
                  <strong>{{ method.name }}</strong>
                  <small>{{ method.description || method.type }}</small>
                </div>
                <span class="type-pill" :class="'type-' + method.type">{{
                  method.type
                }}</span>
              </div>

              <div class="shipping-hint">
                <md-icon>info_outline</md-icon>
                <span>Uncheck all to allow every shipping method. Check specific ones to restrict this product.</span>
              </div>
            </div>
          </div>

          <!-- STEP - Image -->
          <div v-show="currentStep === imageStep" class="step-content">
            <h5 class="section-title">
              {{
                form.productType === "Accessories"
                  ? "Main Product Image (optional)"
                  : "Main Product Image"
              }}
            </h5>
            <div
              class="image-upload-area"
              @click="triggerFileInput"
              @dragover.prevent
              @drop.prevent="onDrop"
            >
              <div v-if="mainImagePreview" class="image-upload-preview">
                <img :src="mainImagePreview" class="preview-img" />
                <button class="remove-preview" @click.stop="removeImage">
                  <md-icon>close</md-icon>
                </button>
              </div>
              <div v-else class="upload-placeholder">
                <md-icon class="upload-cloud-icon">cloud_upload</md-icon>
                <p>Click or drag &amp; drop to upload main image</p>
                <small>PNG, JPG, WEBP up to 5MB</small>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                @change="handleImageUpload"
                style="display: none"
              />
            </div>
          </div>

          <!-- Nav Buttons -->
          <div class="form-nav">
            <button
              class="nav-btn btn-back"
              @click="currentStep--"
              v-if="currentStep > 0"
            >
              <md-icon>arrow_back</md-icon> Back
            </button>
            <div class="nav-spacer" />
            <button
              class="nav-btn btn-next"
              @click="nextStep"
              v-if="currentStep < visibleSteps.length - 1"
            >
              Next <md-icon>arrow_forward</md-icon>
            </button>
            <button
              class="nav-btn btn-submit"
              @click="submitProduct"
              :disabled="submitting"
              v-if="currentStep === visibleSteps.length - 1"
            >
              <span v-if="submitting"
                ><i class="fa fa-spinner fa-spin"></i> Creating...</span
              >
              <span v-else><md-icon>check</md-icon> Create Product</span>
            </button>
          </div>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import ProductService from "../../services/SuperAdmin/Kids/ProductService";
import ShippingService from "../../services/SuperAdmin/Kids/ShippingService";

const STEPS_STANDARD = [
  "Basic Info",
  "Specifications",
  "Pricing & Markets",
  "Shipping",
  "Image",
];
const STEPS_ACCESSORIES = [
  "Basic Info",
  "Specifications",
  "Pricing & Markets",
  "Compatible Products",
  "Shipping",
  "Image",
];
const STEPS_SUBSCRIPTION = ["Basic Info", "Pricing & Markets", "Shipping", "Image"];

export default {
  data() {
    return {
      currentStep: 0,
      submitting: false,
      loadingMarkets: false,
      loadingShipping: false,
      mainImageFile: null,
      mainImagePreview: null,
      marketRows: [],
      shippingMethods: [],
      onetimeProducts: [],
      form: this.defaultForm(),
    };
  },
  computed: {
    visibleSteps() {
      if (this.form.productType === "Accessories") return STEPS_ACCESSORIES;
      if (this.form.productType === "Subscription") return STEPS_SUBSCRIPTION;
      return STEPS_STANDARD;
    },
    compatStep() {
      return this.visibleSteps.indexOf("Compatible Products");
    },
    shippingStep() {
      return this.visibleSteps.indexOf("Shipping");
    },
    imageStep() {
      return this.visibleSteps.indexOf("Image");
    },
  },
  methods: {
    defaultForm() {
      return {
        name: "",
        displayName: "",
        shortDescription: "",
        longDescription: "",
        productType: "Onetime",
        productCategory: "",
        gtin: "",
        recurringInterval: "",
        camera: "",
        screen: "",
        memory: "",
        battery: "",
        productSpecifications: [],
        mainImage: "",
        parentProductIds: [],
        allowedShippingMethods: [],
      };
    },
    onTypeChange() {
      this.currentStep = 0;
      if (this.form.productType === "Accessories") this.loadOnetimeProducts();
    },
    goToStep(i) {
      if (i < this.currentStep) this.currentStep = i;
    },
    nextStep() {
      if (this.currentStep === 0) {
        if (
          !this.form.name.trim() ||
          !this.form.displayName.trim() ||
          !this.form.shortDescription.trim()
        ) {
          this.$toast.error(
            "Name, Display Name and Short Description are required."
          );
          return;
        }
        if (
          this.form.productType !== "Accessories" &&
          !this.form.productCategory
        ) {
          this.$toast.error("Please select a product category.");
          return;
        }
      }
      if (this.currentStep === this.visibleSteps.indexOf("Pricing & Markets")) {
        const enabledMarkets = this.marketRows.filter((r) => r.isEnabled);
        if (enabledMarkets.length === 0) {
          this.$toast.error("Enable at least one marketplace with a price.");
          return;
        }
        if (enabledMarkets.some((r) => !r.price || r.price <= 0)) {
          this.$toast.error("All enabled markets need a valid price.");
          return;
        }
      }
      this.currentStep++;
    },
    toggleShipping(id) {
      const idx = this.form.allowedShippingMethods.indexOf(id);
      if (idx > -1) {
        this.form.allowedShippingMethods.splice(idx, 1);
      } else {
        this.form.allowedShippingMethods.push(id);
      }
    },
    addSpec() {
      this.form.productSpecifications.push({ key: "", value: "" });
    },
    removeSpec(i) {
      this.form.productSpecifications.splice(i, 1);
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.mainImageFile = file;
      this.mainImagePreview = URL.createObjectURL(file);
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0];
      if (!file || !file.type.startsWith("image/")) return;
      this.mainImageFile = file;
      this.mainImagePreview = URL.createObjectURL(file);
    },
    removeImage() {
      this.mainImageFile = null;
      this.mainImagePreview = null;
      this.form.mainImage = "";
    },
    async loadMarkets() {
      this.loadingMarkets = true;
      try {
        const res = await ProductService.marketplaceList();
        const marketplaces = res.data.data.filter((m) => m.isActive);
        this.marketRows = marketplaces.map((m) => ({
          marketplaceId: m._id,
          marketplaceCode: m.code,
          country: m.country,
          icon: m.icon || this.countryFlag(m.code),
          currencyCode: m.currencyId && m.currencyId.code ? m.currencyId.code : "",
          currencySymbol: m.currencyId && m.currencyId.symbol ? m.currencyId.symbol : "",
          currencyId: m.currencyId && m.currencyId._id ? m.currencyId._id : m.currencyId,
          isDefault: m.isDefault,
          isEnabled: m.isDefault,
          price: null,
          offerPrice: null,
          offerPriceText: "",
        }));
      } catch (e) {
        this.$toast.error("Failed to load marketplaces.");
      } finally {
        this.loadingMarkets = false;
      }
    },
    loadShippingMethods() {
      this.loadingShipping = true;
      ShippingService.listMethods()
        .then((r) => {
          this.shippingMethods = (r.data.data || []).filter((m) => m.isActive);
        })
        .catch(() => {})
        .finally(() => {
          this.loadingShipping = false;
        });
    },
    loadOnetimeProducts() {
      ProductService.getOnetimeProducts()
        .then((r) => {
          this.onetimeProducts = r.data.data;
        })
        .catch(() => {});
    },
    countryFlag(code) {
      try {
        return [...code.toUpperCase()]
          .map((c) => String.fromCodePoint(c.charCodeAt(0) + 127397))
          .join("");
      } catch (_) {
        return "🌍";
      }
    },
    async submitProduct() {
      const enabledMarkets = this.marketRows.filter(
        (r) => r.isEnabled && r.price > 0
      );
      if (enabledMarkets.length === 0) {
        this.$toast.error("At least one market with a price is required.");
        return;
      }

      const defaultMarket =
        enabledMarkets.find((r) => r.isDefault) || enabledMarkets[0];

      this.submitting = true;

      if (this.mainImageFile) {
        try {
          const fd = new FormData();
          fd.append("images[]", this.mainImageFile);
          const uploadRes = await ProductService.uploadProductImages(fd);
          const uploaded = uploadRes.data && uploadRes.data.data && uploadRes.data.data[0];
          if (uploaded && uploaded.productImage) {
            this.form.mainImage = uploaded.productImage;
          }
        } catch (e) {
          this.$toast.error("Image upload failed.");
          this.submitting = false;
          return;
        }
      }

      const payload = {
        ...this.form,
        price: defaultMarket.price,
        offerPrice: defaultMarket.offerPrice || null,
        offerPriceText: defaultMarket.offerPriceText || "",
        allowedShippingMethods: this.form.allowedShippingMethods,
        markets: this.marketRows
          .filter((r) => r.isEnabled)
          .map((r) => ({
            marketplaceId: r.marketplaceId,
            marketplaceCode: r.marketplaceCode,
            country: r.country,
            currency: r.currencyCode,
            price: r.price,
            offerPrice: r.offerPrice || null,
            offerPriceText: r.offerPriceText || "",
            isEnabled: true,
          })),
      };

      ProductService.createProduct(payload)
        .then(() => {
          this.$toast.success("Product created successfully!");
          this.$router.push("/kid/wingio-products");
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Create failed."
          );
          this.submitting = false;
        });
    },
  },
  beforeMount() {
    this.loadMarkets();
    this.loadOnetimeProducts();
    this.loadShippingMethods();
  },
};
</script>

<style scoped>
.product-create-page {
  font-family: "Segoe UI", sans-serif;
}
.form-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* Header */
.form-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #43a047 0%, #1b5e20 100%);
  color: white;
}
.header-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.header-icon-wrap .md-icon {
  color: white !important;
  font-size: 26px !important;
}
.form-card-header .title {
  color: white;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
.form-card-header .subtitle {
  color: rgba(255, 255, 255, 0.75);
  margin: 4px 0 0;
  font-size: 13px;
}

/* Steps */
.steps-bar {
  display: flex;
  align-items: center;
  padding: 20px 28px 0;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
  position: relative;
}
.step-item:not(:last-child)::after {
  content: "";
  flex: 1;
  height: 2px;
  background: #e0e0e0;
  margin: 0 8px;
}
.step-item.done:not(:last-child)::after {
  background: #43a047;
}
.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ddd;
  background: white;
  font-size: 13px;
  font-weight: 700;
  color: #aaa;
  flex-shrink: 0;
  transition: all 0.2s;
}
.step-item.active .step-circle {
  border-color: #43a047;
  background: #43a047;
  color: white;
}
.step-item.done .step-circle {
  border-color: #43a047;
  background: #e8f5e9;
  color: #43a047;
}
.step-item.active .step-circle .md-icon,
.step-item.done .step-circle .md-icon {
  color: inherit !important;
  font-size: 16px !important;
}
.step-label {
  font-size: 12px;
  font-weight: 500;
  color: #aaa;
  white-space: nowrap;
}
.step-item.active .step-label {
  color: #43a047;
  font-weight: 600;
}
.step-item.done .step-label {
  color: #555;
}

/* Step content */
.step-content {
  padding: 24px 28px 8px;
  min-height: 280px;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0 0 6px;
}
.section-desc {
  font-size: 13px;
  color: #888;
  margin: 0 0 20px;
}

/* Form */
.form-row {
  display: flex;
  gap: 16px;
}
.form-row.two-col > * {
  flex: 1;
}
.form-group {
  margin-bottom: 18px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.req {
  color: #e53935;
}
.optional {
  color: #aaa;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}
.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}
.field-input:focus {
  border-color: #43a047;
}
.field-input:disabled {
  background: #f5f5f5;
  color: #aaa;
}
.field-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}
.field-select {
  -webkit-appearance: none;
  cursor: pointer;
}

/* Specs */
.specs-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 12px;
}
.specs-divider span {
  font-size: 12px;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  white-space: nowrap;
}
.specs-divider::before,
.specs-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}
.empty-specs {
  text-align: center;
  padding: 20px;
  color: #ccc;
}
.empty-specs .md-icon {
  font-size: 32px !important;
  color: #ddd !important;
  display: block;
  margin-bottom: 4px;
}
.empty-specs p {
  margin: 0;
  font-size: 13px;
}
.spec-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 10px;
}
.spec-input {
  flex: 1;
}
.remove-spec-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: #e53935;
  border-radius: 6px;
}
.remove-spec-btn:hover {
  background: #ffebee;
}
.remove-spec-btn .md-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}
.add-spec-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1.5px dashed #43a047;
  background: transparent;
  border-radius: 8px;
  color: #43a047;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.add-spec-btn:hover {
  background: #e8f5e9;
}
.add-spec-btn .md-icon {
  color: #43a047 !important;
  font-size: 18px !important;
}

/* Market price rows */
.markets-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  color: #888;
  font-size: 14px;
}
.market-price-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 10px;
  transition: all 0.15s;
}
.market-price-row.enabled {
  border-color: #43a047;
  background: #f9fff9;
}
.market-toggle {
  flex-shrink: 0;
}
.market-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 160px;
}
.market-flag {
  font-size: 22px;
}
.market-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}
.market-info small {
  color: #888;
  font-size: 11px;
}
.default-pill {
  background: #43a047;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}
.market-fields {
  display: flex;
  gap: 12px;
  flex: 1;
}
.market-fields.disabled {
  opacity: 0.4;
  pointer-events: none;
}
.price-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.price-field label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.price-field.offer-text-field {
  flex: 1.5;
}
.input-prefix-wrap {
  position: relative;
}
.input-prefix {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 14px;
  pointer-events: none;
}
.prefix-input {
  padding-left: 24px;
  width: 90px;
}

/* Compatible products */
.info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #e3f2fd;
  border-radius: 8px;
  font-size: 14px;
  color: #1565c0;
  margin-bottom: 16px;
}
.parent-products-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
}
.parent-product-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  background: white;
}
.parent-product-card:hover {
  border-color: #9c27b0;
  background: #fdf3ff;
}
.parent-product-card.selected {
  border-color: #9c27b0;
  background: #f3e5f5;
}
.pp-avatar {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}
.pp-info {
  flex: 1;
}
.pp-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}
.pp-info small {
  color: #888;
  font-size: 12px;
}
.pp-check {
  color: #9c27b0 !important;
  font-size: 20px !important;
}

/* Shipping methods */
.shipping-method-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.shipping-method-row:hover {
  border-color: #43a047;
  background: #f9fff9;
}
.shipping-method-row.selected {
  border-color: #43a047;
  background: #e8f5e9;
}
.shipping-check .md-icon {
  color: #43a047 !important;
  font-size: 22px !important;
}
.shipping-icon-wrap {
  width: 36px;
  height: 36px;
  background: #e3f2fd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.shipping-icon-wrap .md-icon {
  color: #0277bd !important;
  font-size: 18px !important;
}
.shipping-info {
  flex: 1;
}
.shipping-info strong {
  display: block;
  font-size: 14px;
  color: #333;
}
.shipping-info small {
  color: #888;
  font-size: 12px;
}
.type-pill {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.type-standard {
  background: #e3f2fd;
  color: #0277bd;
}
.type-next_day {
  background: #fff3e0;
  color: #e65100;
}
.type-express {
  background: #fce4ec;
  color: #c62828;
}
.type-free {
  background: #e8f5e9;
  color: #2e7d32;
}
.shipping-all-banner {
  gap: 8px;
  padding: 10px 14px;
  background: #e8f5e9;
  border-radius: 8px;
  font-size: 13px;
  color: #2e7d32;
  margin-bottom: 12px;
}
.shipping-all-banner .md-icon {
  color: #2e7d32 !important;
  font-size: 18px !important;
}
.shipping-hint {
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  color: #aaa;
}
.shipping-hint .md-icon {
  color: #ccc !important;
  font-size: 16px !important;
}

/* Image */
.image-upload-area {
  border: 2px dashed #ccc;
  border-radius: 12px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
  position: relative;
}
.image-upload-area:hover {
  border-color: #43a047;
  background: #f9fff9;
}
.upload-placeholder {
  text-align: center;
  padding: 40px;
}
.upload-cloud-icon {
  font-size: 48px !important;
  color: #ccc !important;
  margin-bottom: 8px;
  display: block;
}
.upload-placeholder p {
  color: #888;
  margin: 8px 0 4px;
}
.upload-placeholder small {
  color: #bbb;
}
.image-upload-preview {
  position: relative;
}
.preview-img {
  max-height: 200px;
  max-width: 100%;
  border-radius: 8px;
  display: block;
}
.remove-preview {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.remove-preview .md-icon {
  color: white !important;
  font-size: 16px !important;
}

/* Nav */
.form-nav {
  display: flex;
  align-items: center;
  padding: 20px 28px 28px;
  border-top: 1px solid #f0f0f0;
  margin-top: 16px;
}
.nav-spacer {
  flex: 1;
}
.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-back {
  background: #f5f5f5;
  color: #555;
}
.btn-back:hover {
  background: #eee;
}
.btn-back .md-icon {
  color: #555 !important;
  font-size: 18px !important;
}
.btn-next {
  background: #e8f5e9;
  color: #2e7d32;
}
.btn-next:hover {
  background: #c8e6c9;
}
.btn-next .md-icon {
  color: #2e7d32 !important;
  font-size: 18px !important;
}
.btn-submit {
  background: #43a047;
  color: white;
}
.btn-submit:hover:not(:disabled) {
  background: #388e3c;
}
.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.btn-submit .md-icon {
  color: white !important;
  font-size: 18px !important;
}
</style>